/**
 * The styled singleton tooltip.
 *
 * Native `title` tooltips are banned as the explanatory surface: they render as
 * unstylable flat grey boxes and they arrive a second too late. Icon-only
 * controls still carry `title` and `aria-label` for accessibility, and this
 * component lazily migrates that `title` to `data-tip` on first hover so
 * re-rendered DOM needs no changes and the browser stops drawing its own.
 *
 * Three properties make it safe:
 *   - it is ONE element on <body>, so nothing can clip it and there is no
 *     per-trigger cost
 *   - `position: fixed`, so it escapes every overflow container the roster grid
 *     and the drawers create
 *   - `pointer-events: none`, so it is display-only and can never trap a
 *     pointer travelling toward the thing it describes
 *
 * Copy convention: `data-tip="Lead sentence|Supporting detail"`. The lead is
 * bold and near-white; the detail sits one step back.
 */
import { useEffect } from 'react';

const OPEN_DELAY_MS = 90;
const CLOSE_DELAY_MS = 60;
const GAP = 10;

function ensureNode(): HTMLDivElement {
  let el = document.getElementById('sp-tipcard') as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = 'sp-tipcard';
    el.setAttribute('role', 'tooltip');
    el.setAttribute('aria-hidden', 'true');
    el.dataset['open'] = 'false';
    document.body.appendChild(el);
  }
  return el;
}

function render(el: HTMLDivElement, text: string): void {
  const [lead, ...rest] = text.split('|');
  el.replaceChildren();
  const leadEl = document.createElement('span');
  leadEl.className = 'tip-lead';
  leadEl.textContent = (lead ?? '').trim();
  el.appendChild(leadEl);
  const body = rest.join('|').trim();
  if (body) {
    const bodyEl = document.createElement('span');
    bodyEl.className = 'tip-body';
    bodyEl.textContent = body;
    el.appendChild(bodyEl);
  }
}

function place(el: HTMLDivElement, trigger: Element): void {
  const r = trigger.getBoundingClientRect();
  const t = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Prefer above; fall back below when there is no room, which is what happens
  // to the top row of the roster grid and the app bar's own controls.
  const above = r.top - t.height - GAP;
  const side = above > 8 ? 'top' : 'bottom';
  const top = side === 'top' ? above : Math.min(vh - t.height - 8, r.bottom + GAP);

  const wanted = r.left + r.width / 2 - t.width / 2;
  const left = Math.max(8, Math.min(vw - t.width - 8, wanted));

  el.style.top = `${Math.max(8, top)}px`;
  el.style.left = `${left}px`;
  el.dataset['side'] = side;
  // The arrow follows the trigger even when the card was clamped to the viewport
  // edge, so it never points at empty space.
  const arrowX = r.left + r.width / 2 - left;
  el.style.setProperty('--tip-arrow-x', `${Math.max(12, Math.min(t.width - 12, arrowX))}px`);
}

/** Mount once, near the root. Everything after that is attribute-driven. */
export function TipcardHost() {
  useEffect(() => {
    const el = ensureNode();
    let openTimer: number | undefined;
    let closeTimer: number | undefined;
    let current: Element | null = null;

    const hide = () => {
      window.clearTimeout(openTimer);
      current = null;
      el.dataset['open'] = 'false';
      el.setAttribute('aria-hidden', 'true');
    };

    const scheduleHide = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(hide, CLOSE_DELAY_MS);
    };

    const show = (trigger: Element, text: string) => {
      current = trigger;
      render(el, text);
      el.dataset['open'] = 'true';
      el.setAttribute('aria-hidden', 'false');
      // Two frames: one for the text to lay out, one to measure the result.
      requestAnimationFrame(() => {
        if (current === trigger) place(el, trigger);
      });
    };

    const resolve = (target: EventTarget | null): { el: Element; text: string } | null => {
      if (!(target instanceof Element)) return null;
      const trigger = target.closest<HTMLElement>('[data-tip], [title]');
      if (!trigger) return null;
      // Lazy migration: take the native title away so the browser stops drawing
      // its own box, but keep the text as the tip and keep aria-label intact.
      const native = trigger.getAttribute('title');
      if (native && !trigger.dataset['tip']) {
        trigger.dataset['tip'] = native;
        if (!trigger.getAttribute('aria-label')) trigger.setAttribute('aria-label', native.split('|')[0] ?? native);
        trigger.removeAttribute('title');
      }
      const text = trigger.dataset['tip'];
      return text ? { el: trigger, text } : null;
    };

    const onOver = (e: Event) => {
      const found = resolve(e.target);
      if (!found) return;
      if (found.el === current) {
        window.clearTimeout(closeTimer);
        return;
      }
      window.clearTimeout(closeTimer);
      window.clearTimeout(openTimer);
      openTimer = window.setTimeout(() => show(found.el, found.text), OPEN_DELAY_MS);
    };

    const onOut = (e: Event) => {
      const found = resolve(e.target);
      if (found) scheduleHide();
    };

    // Keyboard users get the same card on focus, which is the whole point of
    // not relying on a hover-only affordance.
    const onFocus = (e: FocusEvent) => {
      const found = resolve(e.target);
      if (found) show(found.el, found.text);
    };

    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerout', onOut, true);
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('focusout', hide, true);
    // Any of these can leave the card pointing at something that has moved.
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Escape') hide();
    });

    return () => {
      document.removeEventListener('pointerover', onOver, true);
      document.removeEventListener('pointerout', onOut, true);
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('focusout', hide, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      document.removeEventListener('pointerdown', hide, true);
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };
  }, []);

  return null;
}
